import {
  AspectRatio,
  Box,
  HStack,
  IconButton,
  Input,
  Link,
  Text,
  VStack,
  useNumberInput,
  useToast,
} from "@chakra-ui/react";
import React, { FC, useMemo, useState, useEffect } from "react";
import ADD_TO_CART from "../../types/AddToCard";
import { useAppState } from "../../context";
import { useMutation } from "@apollo/client";

import { calculateDiscountPercentage } from "../../utils/shopUtils";
import {
  GatsbyImage,
  getImage,
  IGatsbyImageData,
  StaticImage,
} from "gatsby-plugin-image";
import { MdShoppingCart } from "../../customIcons";

type HorizontalProductCardType = {
  name: string;
  price: string;
  salePrice: string;
  productId: number;
  slug: string;
  regularPrice: string;
  tierPrice: any;
  altText: string;
  imageData: IGatsbyImageData;
};

const HorizontalProductCard: FC<HorizontalProductCardType> = ({
  name,
  price,
  salePrice,
  imageData,
  productId,
  slug,
  regularPrice,
  tierPrice,
  altText,
}) => {
  const toast = useToast();
  const { setCart } = useAppState();

  const tierPricesData = useMemo(() => {
    if (!tierPrice) {
      return null;
    }

    return [
      {
        discount: `${calculateDiscountPercentage(
          tierPrice?.[0]?.price,
          tierPrice?.[0]?.price,
        )}%`,
        price: tierPrice?.[0]?.price2,
      },
      {
        discount: `${calculateDiscountPercentage(
          tierPrice?.[0]?.price,
          tierPrice?.[0]?.price2,
        )}%`,
        price: tierPrice?.[0]?.price2,
      },
      {
        discount: `${calculateDiscountPercentage(
          tierPrice?.[0]?.price,
          tierPrice?.[0]?.price3,
        )}%`,
        price: tierPrice?.[0]?.price3,
      },
      {
        discount: `${calculateDiscountPercentage(
          tierPrice?.[0]?.price,
          tierPrice?.[0]?.price4,
        )}%`,
        price: tierPrice?.[0]?.price4,
      },
    ];
  }, [tierPrice]);

  // Function to handle product click
  const handleProductClick = () => {
    // Google Analytics event logic
    const productInfoForGA = {
      item_name: name,
      item_id: productId,
      price: price ? parseFloat(price?.replace("$", "")) : undefined,
      // item_category: props.productCategories,
      // item_variant: props.variant,
      item_list_name: "Shop page", // Adjust this based on your page
      item_list_id: "shop_page", // Adjust this based on your page
      quantity: 1,
    };

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: "select_item",
      ecommerce: {
        items: [productInfoForGA],
      },
    });
  };

  // useEffect to attach click event to the product card
  useEffect(() => {
    const productCardElement = document.getElementById(
      `product-card-${productId}`,
    );
    if (productCardElement) {
      productCardElement.addEventListener("click", handleProductClick);

      // Cleanup the event listener on component unmount
      return () => {
        productCardElement.removeEventListener("click", handleProductClick);
      };
    }
  }, [productId]);

  const [addToCart, { loading }] = useMutation(ADD_TO_CART, {
    onCompleted: ({ addToCart }) => {
      toast({
        title: (
          <Text>
            Added to&nbsp;
            <Link
              href="https://www.medsupplysolutions.com/cart/"
              fontWeight={700}
              textDecoration="underline"
            >
              cart
            </Link>
          </Text>
        ),
        status: "success",
      });
      setCart(addToCart.cart);

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          items: [
            {
              item_name: name,
              item_id: productId,
              price: price ? parseFloat(price?.replace("$", "")) : undefined,
              quantity: "1",
            },
          ],
        },
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "There was an error adding your product",
        status: "error",
      });
    },
  });

  const [value, setValue] = useState(1);

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: 1,
      defaultValue: 1,
      min: 1,
      max: 999,
      precision: 0,
      value: value,
      onChange: (e: any) => setValue(e),
    });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  const formattedPrice = `$${regularPrice.replace("$", "")}`;

  const {
    priceToShow,
  }: { priceToShow: string | undefined; selectedAmountId: number | undefined } =
    useMemo(() => {
      if (!tierPricesData) {
        return {
          priceToShow: regularPrice as string,
          selectedAmountId: undefined,
        };
      }

      if (value >= 21) {
        return {
          priceToShow: `$${tierPricesData[3].price}.00`,
          selectedAmountId: 3,
        };
      }

      if (value >= 11) {
        return {
          priceToShow: `$${tierPricesData[2].price}.00`,
          selectedAmountId: 2,
        };
      }

      if (value >= 3) {
        return {
          priceToShow: `$${tierPricesData[1].price}.00`,
          selectedAmountId: 1,
        };
      }

      if (value < 3) {
        return {
          priceToShow: regularPrice,
          selectedAmountId: undefined,
        };
      }

      return {
        priceToShow: regularPrice,
        selectedAmountId: undefined,
      };
    }, [value, tierPricesData, regularPrice]);

  const handleAddToCart = () => {
    addToCart({
      variables: { input: { productId, quantity: Number(input.value) } },
    });
  };

  const image = imageData ? getImage(imageData) : null;

  return (
    <Link
      href={`https://www.medsupplysolutions.com/product/${slug}/`}
      isExternal
      style={{ textDecoration: "none" }}
    >
      <HStack
        bg="#fff"
        id={`product-card-${productId}`}
        cursor="pointer"
        border="1px solid #E1E7EF"
        borderRadius="8px"
        w={{ base: "303px", sm: "355px" }}
      >
        <Box
          w={{ base: "100px", sm: "120px" }}
          h={{ base: "100px", sm: "120px" }}
        >
          <AspectRatio ratio={1 / 1}>
            {image ? (
              <GatsbyImage
                image={image}
                alt={altText || "Product image"}
                style={{ height: "120px", width: "120px" }}
              />
            ) : (
              <StaticImage
                rel="preload"
                src="../../../images/productplaceholder.png"
                alt="Placeholder"
                style={{
                  height: "120px",
                  width: "120px",
                  backgroundColor: "transparent !important",
                }}
              />
            )}
          </AspectRatio>
        </Box>
        <VStack
          flex={1}
          py={3}
          h="100%"
          justifyContent="flex-start"
          gap={0}
          alignItems="flex-start"
        >
          <Text
            noOfLines={1}
            w={{ base: "200px" }}
            fontWeight={500}
            _hover={{ textDecoration: "underline" }}
          >
            {name}
          </Text>

          {salePrice ? (
            <HStack>
              <Text
                fontSize="lg"
                fontWeight="600"
                _hover={{ textDecoation: "none" }}
              >
                {salePrice}
              </Text>
              <Text fontSize="14px" color="#4A5568" textDecor="line-through">
                {formattedPrice}
              </Text>
            </HStack>
          ) : (
            <HStack>
              <Text
                fontSize="lg"
                fontWeight="600"
                _hover={{ textDecoation: "none" }}
              >
                {`$${priceToShow.replace("$", "")}`}
              </Text>
            </HStack>
          )}

          <HStack
            pt={3}
            justifyContent={{ base: "space-between", md: "unset" }}
          >
            <>
              <HStack
                gap="0"
                w={{ base: "85px", sm: "100px" }}
                border="1px solid #CBD5E0"
                borderRadius="6px"
                h="40px"
                p={0}
                justifyContent="space-between"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Input
                  value="-"
                  {...dec}
                  fontSize="2xl"
                  p={0}
                  w={10}
                  textAlign="center"
                  border="none"
                  color={input.value === "999" ? "#728197" : "#8353C4"}
                  cursor={input.value === "999" ? "not-allowed" : ""}
                />
                <Input
                  maxLength={3}
                  focusBorderColor="transparent"
                  border="none"
                  p={0}
                  w={8}
                  {...input}
                  textAlign="center"
                />
                <Input
                  value="+"
                  {...inc}
                  fontSize="2xl"
                  p={0}
                  w={10}
                  textAlign="center"
                  border="none"
                  color={input.value === "999" ? "#728197" : "#8353C4"}
                  cursor={input.value === "999" ? "not-allowed" : ""}
                />
              </HStack>

              <IconButton
                aria-label="cart"
                borderRadius="6px"
                h={10}
                w={{ base: "85px", sm: "100px" }}
                justifyContent="center"
                color="#fff"
                background="#3B2060"
                _hover={{ background: "#6A3AAB", color: "#fff" }}
                fontSize="sm"
                fontWeight="600"
                icon={<MdShoppingCart width="20px" height="20px" fill="#fff" />}
                isLoading={loading}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddToCart();
                }}
              />
            </>
          </HStack>
        </VStack>
      </HStack>
    </Link>
  );
};

export default HorizontalProductCard;
